import { AnalyticsService } from "@/lib/api-generated";
import { OpenAPI } from "@/lib/api-generated/core/OpenAPI";

/**
 * Frontend performance monitoring
 * Tracks performance metrics and reports to backend
 */

interface PerformanceMetric {
  name: string;
  type: 'navigation' | 'resource' | 'measure' | 'paint';
  value: number;
  timestamp: number;
}

interface LayoutShiftEntry extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];

  /**
   * Monitor Core Web Vitals
   */
  monitorWebVitals(): void {
    // Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        this.reportMetric({
          name: 'LCP',
          type: 'paint',
          value: (lastEntry as PerformancePaintTiming).startTime,
          timestamp: Date.now(),
        });
      });

      try {
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // Browser doesn't support LCP
      }
    }

    // First Input Delay (FID)
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.reportMetric({
            name: 'FID',
            type: 'measure',
            value: (entry as PerformanceEventTiming).processingStart - entry.startTime,
            timestamp: Date.now(),
          });
        });
      });

      try {
        observer.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // Browser doesn't support FID
      }
    }

    // Cumulative Layout Shift (CLS)
    if ('PerformanceObserver' in window) {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const layoutShift = entry as LayoutShiftEntry;
          if (!layoutShift.hadRecentInput) {
            clsValue += layoutShift.value;
          }
        });

        this.reportMetric({
          name: 'CLS',
          type: 'measure',
          value: clsValue,
          timestamp: Date.now(),
        });
      });

      try {
        observer.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // Browser doesn't support CLS
      }
    }
  }

  /**
   * Measure custom performance
   */
  measure(label: string, fn: () => void): void {
    if ('performance' in window && 'mark' in performance) {
      const startMark = `start-${label}`;
      const endMark = `end-${label}`;

      performance.mark(startMark);
      fn();
      performance.mark(endMark);

      try {
        performance.measure(label, startMark, endMark);
        const measure = performance.getEntriesByName(label)[0];
        
        this.reportMetric({
          name: label,
          type: 'measure',
          value: measure.duration,
          timestamp: Date.now(),
        });
      } catch (e) {
        console.warn('Performance measurement failed:', e);
      }
    }
  }

  /**
   * Monitor React component render performance
   */
  measureComponent(componentName: string, renderFn: () => void): void {
    this.measure(`component-${componentName}`, renderFn);
  }

  /**
   * Report metric to backend
   */
  private async reportMetric(metric: PerformanceMetric): Promise<void> {
    this.metrics.push(metric);

    // Batch send metrics every 10 seconds
    if (this.metrics.length >= 10) {
      await this.flushMetrics();
    }
  }

  /**
   * Flush metrics to backend
   */
  async flushMetrics(): Promise<void> {
    if (this.metrics.length === 0) return;

    const metricsToSend = [...this.metrics];
    this.metrics = [];

    try {
      const isProd = import.meta.env.MODE === 'production';
      if (isProd) {
        const hasToken = !!OpenAPI.TOKEN;
        if (hasToken) {
          await AnalyticsService.postFrontendMetricsApiV1AnalyticsMetricsPost({
            metrics: metricsToSend
          });
        }
      }
    } catch (error) {
      console.warn('Failed to send metrics:', error);
    }
  }

  /**
   * Flush metrics optimized for unload using sendBeacon when available
   */
  flushMetricsBeacon(): void {
    if (this.metrics.length === 0) return;
    const metricsToSend = [...this.metrics];
    this.metrics = [];

    try {
      const isProd = import.meta.env.MODE === 'production';
      // Only send beacon if we have a token (authenticated session)
      const hasToken = !!OpenAPI.TOKEN;
      
      if (isProd && hasToken) {
        const base = OpenAPI.BASE.replace(/\/$/, '');
        const url = `${base}/api/v1/analytics/metrics`;
        const payload = JSON.stringify({ metrics: metricsToSend });
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OpenAPI.TOKEN}`
        };

        if ('navigator' in window && 'sendBeacon' in navigator) {
          // Beacon doesn't support custom headers easily without Blob, 
          // and we need Auth header. So we might skip Beacon for auth-required endpoints 
          // or rely on cookies if that was an option (it's not here).
          // For now, let's use fetch with keepalive which is the modern replacement.
          fetch(url, {
            method: 'POST',
            headers,
            body: payload,
            keepalive: true,
          });
        } else {
          fetch(url, {
            method: 'POST',
            headers,
            body: payload,
            keepalive: true,
          });
        }
      }
    } catch (error) {
      // Swallow errors on unload
    }
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Start monitoring on page load
if (typeof window !== 'undefined') {
  performanceMonitor.monitorWebVitals();

  window.addEventListener('pagehide', () => {
    performanceMonitor.flushMetricsBeacon();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      performanceMonitor.flushMetricsBeacon();
    }
  });
}
